// src/context/BranchContext.js
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import api from "../utils/api";

const BranchContext = createContext();

const formatDate = (date) => {
  if (!date || isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  const hours = `${date.getHours()}`.padStart(2, "0");
  const minutes = `${date.getMinutes()}`.padStart(2, "0");
  const seconds = `${date.getSeconds()}`.padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

export const BranchProvider = ({ children }) => {
  const [selectedBranch, setSelectedBranch] = useState("All");
  const [branchList, setBranchList] = useState([]);
  // machine_name (display name) → branch name
  const [machineToBranchMap, setMachineToBranchMap] = useState({});
  // meter_id / hierarchy node key → branch name
  const [meterIdToBranchMap, setMeterIdToBranchMap] = useState({});
  const [hierarchyTree, setHierarchyTree] = useState(null);
  const [loading, setLoading] = useState(false);

  const lastFetchedUserRef = useRef(null);
  const isFetchingRef = useRef(false);

  const fetchBranches = useCallback(async (force = false) => {
    const username = localStorage.getItem("username");
    if (!username) {
      setBranchList([]);
      setMachineToBranchMap({});
      setMeterIdToBranchMap({});
      lastFetchedUserRef.current = null;
      return;
    }

    if (!force && lastFetchedUserRef.current === username) {
      return;
    }
    if (isFetchingRef.current) return;

    isFetchingRef.current = true;
    setLoading(true);

    try {
      const response = await api.get(`/hierarchy-tree?username=${encodeURIComponent(username)}`);
      const rawTree = response.data?.data || response.data;

      if (rawTree) {
        // Flatten the nested hierarchy tree into the same format expected below
        const flatNodes = [];
        const walk = (node) => {
          if (!node || typeof node !== "object") return;
          if (node.meter_id || node.machine_name) {
            flatNodes.push(node);
          }
          if (node.children) {
            const children = Array.isArray(node.children) ? node.children : Object.values(node.children);
            children.forEach(walk);
          }
        };
        walk(rawTree);

        // 1. Collect all branch nodes
        const branches = flatNodes
          .filter((data) => data.is_branch)
          .map((data) => ({
            id: data.meter_id || data.machine_name,
            name: data.machine_name || data.name,
            hierarchy_path: data.hierarchy_path || "",
          }));
        setBranchList(branches);

        // Sort longest path first so child-branches match before parent-branches
        const sortedBranches = [...branches].sort(
          (a, b) => b.hierarchy_path.length - a.hierarchy_path.length
        );

        const nameMap = {};   // machine_name → branch name
        const meterMap = {};  // meter_id / node key → branch name

        flatNodes.forEach((nodeData) => {
          if (nodeData.is_branch) return; // skip the branch nodes themselves

          const nodePath = nodeData.hierarchy_path || "";
          const parentBranch = sortedBranches.find(
            (b) =>
              b.hierarchy_path &&
              nodePath.startsWith(b.hierarchy_path) &&
              nodePath !== b.hierarchy_path
          );
          if (!parentBranch) return;

          // Map by display name
          const nodeName = nodeData.machine_name || nodeData.name;
          if (nodeName) nameMap[nodeName] = parentBranch.name;

          // Map by hierarchy node key (often equals meter_id in /client-machines)
          const nodeId = nodeData.meter_id || nodeData.machine_name;
          if (nodeId) meterMap[nodeId] = parentBranch.name;

          // Also map by the explicit meter_id field on the node, if present
          if (nodeData.meter_id) {
            meterMap[String(nodeData.meter_id)] = parentBranch.name;
          }
        });

        setMachineToBranchMap(nameMap);
        setMeterIdToBranchMap(meterMap);
        setHierarchyTree(rawTree);
      }
      lastFetchedUserRef.current = username;
    } catch (error) {
      console.error("Failed to fetch branches in global context:", error);
      lastFetchedUserRef.current = username;
    } finally {
      isFetchingRef.current = false;
      setLoading(false);
    }
  }, []);

  const refreshBranches = useCallback(() => fetchBranches(true), [fetchBranches]);

  useEffect(() => {
    fetchBranches();

    // Listen to storage/auth changes (login, logout, auth events)
    const handleStorageChange = (e) => {
      if (!e || e.key === "username" || e.key === "token" || e.type === "auth-change") {
        fetchBranches(true);
      }
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("auth-change", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("auth-change", handleStorageChange);
    };
  }, [fetchBranches]);

  return (
    <BranchContext.Provider
      value={{
        selectedBranch,
        setSelectedBranch,
        branchList,
        setBranchList,
        machineToBranchMap,
        meterIdToBranchMap,
        hierarchyTree,
        loading,
        refreshBranches,
      }}
    >
      {children}
    </BranchContext.Provider>
  );
};

export const useBranch = () => {
  const context = useContext(BranchContext);
  if (!context) {
    throw new Error("useBranch must be used within a BranchProvider");
  }
  return context;
};
